<template>
  <div
    id="annotationToolRibbon"
    class="ribbon"
  >
    <div class="diagonal-box">
      <p>AGTG</p>
    </div>
    <div class="ribbon__group">
      <!-- Commented out because confusing -->
      <!-- <div id="pending-annotation-counts">
        <div class="tag">
          <div class="tag__count"><span>{{ sam2Annotations.length }}</span></div>
          <span class="tag__label ms-2">Segmentations</span>
        </div>
        <div class="tag">
          <div class="tag__count"><span>{{ aigt2dAnnotations.length }}</span></div>
          <span class="tag__label ms-2">2D Boxes</span>
        </div>
        <div class="tag">
          <div class="tag__count"><span>{{ aigt3dAnnotations.length }}</span></div>
          <span class="tag__label ms-2">3D Boxes</span>
        </div>
      </div> -->
      <!-- <IconButton
        id="undoBtn"
        class="ribbon__button"
        aria-label="Undo"
        :disabled="!canUndo"
        :icon="'undo'"
        :showHover="false"
        :type="''"
        :title="'Undo'"
        @click="handleUndo"
      /> -->
    </div>
    <div class="ribbon__group">
      <hr class="ribbon__vr">
      <button
        class="button ribbon__button-secondary button-rounded"
        :class="{'button-spinner': isCreatingAnnotations}"
        :disabled="!hasInitState || isPropagating || isCreatingAnnotations || sam2Annotations.length === 0"
        :title="`Clear unsaved annotations`"
        @click="handleClearSAM2Annotations"
      >
        Clear Annotations
      </button>
      <button
        v-tooltip="`Save pending annotations`"
        class="button ribbon__button-primary button-rounded"
        :class="{'button-spinner': isCreatingAnnotations}"
        :disabled="!hasInitState || isPropagating || isCreatingAnnotations || sam2Annotations.length <= 0"
        @click="handleSaveSAM2Annotations"
      >
        Save Annotations
      </button>
    </div>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useEditorStore } from '@/stores/useEditorStore.js';
import useViewerVisualizations from '@/composables/useViewerVisualizations.js';
import { useSAM2Store } from '@/stores/useSAM2Store.js';

const emit = defineEmits([
  'submit',
]);

const editorStore = useEditorStore();
const {
  labels,
} = storeToRefs(editorStore);

const {
  addNewAnnotationsArrayToCache,
} = useViewerVisualizations();

const sam2Store = useSAM2Store();
const {
  sam2Options,
  sam2Annotations,
  aigt3dAnnotations,
  aigt2dAnnotations,
  hasInitState,
  isPropagating,
  isCreatingAnnotations,
} = storeToRefs(sam2Store);
const {
  handleSAMReset,
} = sam2Store;

async function handleSaveSAM2Annotations() {
  isCreatingAnnotations.value = true;
  const dataConnect = new DatastoreConnect();
  return dataConnect.addAnnotationsBulk({ annotations: [...sam2Annotations.value, ...aigt3dAnnotations.value, ...aigt2dAnnotations.value] })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error);
      }

      return resp.result;
    })
    .then(async (newAnnotations) => {
      // Set label_index and label_name for new annotations
      // TODO: remove the necessity to do this
      newAnnotations.forEach((anno) => {
        const label = labels.value.find((e) => e.id === anno.label_id);
        anno.label_index = label.index;
        anno.label_name = label.name;
      });

      // Update cache with new annotations
      addNewAnnotationsArrayToCache(newAnnotations);

      // Reset SAM2
      await handleSAMReset();

      isCreatingAnnotations.value = false;

      emit('submit');
    })
    .catch((error) => {
      isCreatingAnnotations.value = false;
      throw error;
    });
}

async function handleClearSAM2Annotations() {
  sam2Options.value.params.clicks = [];
  sam2Options.value.params.box = [];
  sam2Options.value.params.boxes = [];
  sam2Annotations.value = [];
  aigt3dAnnotations.value = [];
  aigt2dAnnotations.value = [];
}

defineExpose({
  handleSaveSAM2Annotations,
});

</script>

<style lang="scss" scoped>
.diagonal-box {
  background-color: var(--color-primary-400);
  color: var(--primary-text-color);
  width: 100%;
  height: 100%;
  max-width: 100px;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 0% 100%);
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  padding-right: 34px;
  font-weight: 500;
}

.ribbon {
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  min-height: 37px;
  max-height: 37px;
  padding: 2px 16px;
  justify-content: space-between;
  z-index: 10;
  box-shadow: 0 3px 3px -3px gray, 0 10px 10px -10px gray;
  color: var(--ribbon-icons-color);
  background: var(--ribbon-bg);
  border-left: solid 1px var(--ribbon-border);

  &__group {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  &__vr {
    display: flex;
    width: 1px;
    height: 60%;
    margin: 0;
  }

  &__button {
    display: flex;
    min-width: 22px;
    height: 22px;
    color: inherit;
    background: none;
    border: none;
    font-size: 0.875rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    align-items: center;
    cursor: pointer;

    &:disabled {
      cursor: default;
      color: var(--color-disabled);
    }
  }

  &__button-primary {
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;

    &:hover:not(:disabled), &.active:not(:disabled) {
      background-color: var(--button-hover-color);
    }
  }

  &__button-secondary {
    background: white;
    @include themify() {
      color: themed('body-text-color')
    }
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &:disabled {
      cursor: default;
      color: var(--button-text-color);
    }
  }

  &__labeled_button {
    width: auto;
  }
}

:deep(.filter-button) {
  height: 22px;
}

#pending-annotation-counts {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  margin-left: 92px;
}

.tag {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 18px;
  border: 2px solid var(--color-primary);
  padding: 2px 4px;

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    padding: 0 6px;
    height: 18px;
    width: fit-content;
    background: var(--color-accent);
  }

  &__label {
    font-weight: 600;
    letter-spacing: 0.025em;
    font-size: 0.875rem;
    line-height: 1;
  }
}

</style>
